import { useState, useMemo } from 'react';
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Search,
  Download,
  Users,
  Sparkles,
  ShieldCheck,
  RotateCcw,
  Edit2,
  Check,
  LayoutGrid,
  Table as TableIcon,
  Wrench,
  Shield,
  Car,
  Calendar,
  Phone,
} from 'lucide-react';
import Button from '@components/ui/Button.js';
import SurfaceCard from '@components/ui/SurfaceCard.js';
import Input from '@components/ui/Input.js';
import { cn } from '@utils/Cn.js';
import type { BulkImportResult, ImportPreviewRow } from '../types/index.js';
import EditRowModal from './EditRowModal.js';

interface DataReviewGridProps {
  result: BulkImportResult;
  fileName: string;
  onConfirmImport: () => void;
  onBackToMapping: () => void;
  onDownloadReport: () => void;
  onRowOverride: (rowNumber: number, field: string, value: string) => void;
  onRowMultiOverride?: (rowNumber: number, updates: Record<string, unknown>) => void;
  onBatchResolveIssues?: (fixes: Record<number, Record<string, unknown>>) => void;
  isImporting: boolean;
}

type FilterTab = 'issues' | 'all' | 'ready' | 'associates';
type ViewMode = 'cards' | 'table';

function getPolicyTypeBadgeClass(typeName: string): string {
  const norm = (typeName || '').toUpperCase().trim();
  if (norm.includes('MOTOR')) {
    return 'bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/25';
  }
  if (norm.includes('HEALTH') || norm.includes('MEDICLAIM')) {
    return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/25';
  }
  if (norm.includes('WC') || norm.includes('WORKMEN') || norm.includes('FIRE') || norm.includes('GROUP')) {
    return 'bg-amber-500/10 text-amber-800 dark:text-amber-300 border-amber-500/25';
  }
  return 'bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/25';
}

export default function DataReviewGrid({
  result,
  fileName,
  onConfirmImport,
  onBackToMapping,
  onDownloadReport,
  onRowOverride,
  onRowMultiOverride,
  onBatchResolveIssues,
  isImporting,
}: DataReviewGridProps) {
  const rowStatuses = result.rowStatuses;

  const counts = useMemo(() => {
    let ready = 0;
    let issues = 0;
    let associates = 0;
    for (const r of rowStatuses) {
      if (r.status === 'FAILED') issues++;
      else ready++;
      if (r.status === 'ASSOCIATE' || r.isAssociatePolicy) associates++;
    }
    return { all: rowStatuses.length, ready, issues, associates };
  }, [rowStatuses]);

  // Default to 'issues' tab if issues exist, otherwise 'all'
  const [activeTab, setActiveTab] = useState<FilterTab>(counts.issues > 0 ? 'issues' : 'all');
  const [viewMode, setViewMode] = useState<ViewMode>(counts.issues > 0 ? 'cards' : 'table');
  const [searchQuery, setSearchQuery] = useState('');

  // Modal editing state
  const [selectedRowForModal, setSelectedRowForModal] = useState<ImportPreviewRow | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // In-cell quick editing state
  const [editingCell, setEditingCell] = useState<{ rowNumber: number; field: string } | null>(null);
  const [editValue, setEditValue] = useState('');

  const failedRows = useMemo(() => {
    return rowStatuses.filter((r) => r.status === 'FAILED');
  }, [rowStatuses]);

  // Check if any issues are auto-fixable vehicle keywords like GROUP or AUTO
  const autoFixableCount = useMemo(() => {
    return failedRows.filter((r) => {
      const v = (r.vehicleNumber ?? '').toUpperCase().trim();
      return v === 'GROUP' || v.includes('GROUP') || v === 'AUTO';
    }).length;
  }, [failedRows]);

  const filteredRows = useMemo(() => {
    return rowStatuses.filter((row) => {
      // Tab filter
      if (activeTab === 'ready' && row.status === 'FAILED') return false;
      if (activeTab === 'issues' && row.status !== 'FAILED') return false;
      if (activeTab === 'associates' && !row.isAssociatePolicy && row.status !== 'ASSOCIATE') return false;

      // Search filter
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase().trim();
      return (
        row.clientName.toLowerCase().includes(q) ||
        (row.mobileNumber?.toLowerCase().includes(q) ?? false) ||
        (row.vehicleNumber?.toLowerCase().includes(q) ?? false) ||
        (row.policyNumber?.toLowerCase().includes(q) ?? false) ||
        row.policyTypeName.toLowerCase().includes(q) ||
        (row.referenceName?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [rowStatuses, activeTab, searchQuery]);

  // ─── Modal Open / Save Handlers ───────────────────────────────────────────
  function openEditModal(row: ImportPreviewRow) {
    setSelectedRowForModal(row);
    setIsEditModalOpen(true);
  }

  function handleSaveRowFromModal(rowNumber: number, updates: Record<string, unknown>) {
    if (onRowMultiOverride) {
      onRowMultiOverride(rowNumber, updates);
    } else {
      // Fallback: apply each field individually
      for (const [key, val] of Object.entries(updates)) {
        let strVal = '';
        if (typeof val === 'string') {
          strVal = val;
        } else if (typeof val === 'number' || typeof val === 'boolean') {
          strVal = String(val);
        }
        onRowOverride(rowNumber, key, strVal);
      }
    }
  }

  function handleSaveAndNext(rowNumber: number, updates: Record<string, unknown>) {
    handleSaveRowFromModal(rowNumber, updates);
    // Find next failed row
    const nextFailed = failedRows.find((r) => r.rowNumber !== rowNumber);
    if (nextFailed) {
      setSelectedRowForModal(nextFailed);
    } else {
      setIsEditModalOpen(false);
      setSelectedRowForModal(null);
    }
  }

  // ─── 1-Click Batch Auto-Fix ────────────────────────────────────────────────
  function handleRunAutoFixCommonIssues() {
    if (!onBatchResolveIssues) return;
    const fixes: Record<number, Record<string, unknown>> = {};

    for (const r of failedRows) {
      const v = (r.vehicleNumber ?? '').toUpperCase().trim();
      if (v === 'GROUP' || v.includes('GROUP')) {
        fixes[r.rowNumber] = {
          policyTypeName: 'Group Insurance',
          vehicleNumber: null,
        };
      } else if (v === 'AUTO') {
        fixes[r.rowNumber] = {
          policyTypeName: 'Motor',
          vehicleNumber: null,
        };
      }
    }

    if (Object.keys(fixes).length > 0) {
      onBatchResolveIssues(fixes);
    }
  }

  // ─── In-Cell Double Click Editing ─────────────────────────────────────────
  function startEditing(rowNumber: number, field: string, initialVal: string) {
    setEditingCell({ rowNumber, field });
    setEditValue(initialVal);
  }

  function commitEdit() {
    if (editingCell) {
      onRowOverride(editingCell.rowNumber, editingCell.field, editValue.trim());
      setEditingCell(null);
      setEditValue('');
    }
  }

  function cancelEdit() {
    setEditingCell(null);
    setEditValue('');
  }

  return (
    <div className="space-y-3.5 w-full max-w-full mx-auto">
      {/* ─── Top Header & Controls ───────────────────────────────────────────── */}
      <SurfaceCard className="p-3.5 border border-line/60 space-y-3 shadow-2xs">
        {/* Title row */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
          <div className="flex items-center gap-2.5 flex-wrap">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-500/10 text-green-600 dark:text-green-400">
              <ShieldCheck size={18} />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-sm font-bold text-ink">Step 3: Review &amp; Quick-Fix Data</h3>
                <span className="font-mono text-[11px] px-2 py-0.5 rounded-md bg-slate/10 text-ink-soft">
                  {fileName}
                </span>
                <span className="text-[11px] font-semibold text-ink-faint">
                  &bull; {String(result.totalRows)} records analyzed
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onDownloadReport}
              leftIcon={<Download size={12} />}
              className="text-xs h-8 px-2.5 font-semibold"
            >
              Export Report
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onBackToMapping}
              leftIcon={<RotateCcw size={12} />}
              className="text-xs h-8 px-2.5 font-semibold"
            >
              Adjust Mapping
            </Button>
          </div>
        </div>

        {/* ─── Health / Attention Banner ───────────────────────────────────────── */}
        {counts.issues > 0 ? (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
            <div className="flex items-center gap-2.5">
              <div className="h-7 w-7 rounded-lg bg-amber-500/20 text-amber-700 dark:text-amber-300 flex items-center justify-center shrink-0">
                <AlertTriangle size={15} />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-bold text-amber-900 dark:text-amber-100">
                    {String(counts.issues)} Records Need Attention
                  </span>
                  <span className="px-1.5 py-0.2 rounded-full text-[9px] font-extrabold uppercase bg-amber-500 text-white tracking-wide">
                    Action Recommended
                  </span>
                </div>
                <p className="text-[11px] text-amber-700/90 dark:text-amber-300/90 mt-0.5">
                  Format issues detected in {String(counts.issues)} rows. Apply 1-click fixes below or import the {String(counts.ready)} valid records.
                </p>
              </div>
            </div>

            {/* Action Buttons on Banner */}
            <div className="flex items-center gap-2 shrink-0">
              {autoFixableCount > 0 && onBatchResolveIssues && (
                <Button
                  size="sm"
                  variant="primary"
                  onClick={handleRunAutoFixCommonIssues}
                  leftIcon={<Sparkles size={12} />}
                  className="font-bold text-xs h-8 px-3 bg-amber-600 hover:bg-amber-700 text-white border-none shadow-xs"
                >
                  1-Click Auto-Fix ({String(autoFixableCount)})
                </Button>
              )}

              {failedRows[0] && (() => {
                const firstFailed = failedRows[0];
                return (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => { openEditModal(firstFailed); }}
                    leftIcon={<Wrench size={12} />}
                    className="font-bold text-xs h-8 px-3"
                  >
                    Fix Issues (Guided)
                  </Button>
                );
              })()}
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-green-500/30 bg-green-500/10 px-3 py-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="h-7 w-7 rounded-lg bg-green-500/20 text-green-700 dark:text-green-300 flex items-center justify-center shrink-0">
                <CheckCircle2 size={16} />
              </div>
              <div>
                <h4 className="text-xs font-bold text-green-800 dark:text-green-200">
                  All {String(counts.all)} Records Are Valid &amp; Ready to Import!
                </h4>
                <p className="text-[11px] text-green-700 dark:text-green-300">
                  Every policy and contact number passed verification. Click Confirm &amp; Import below.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ─── Filters & View Toggle Bar ───────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 pt-1">
          {/* Tabs */}
          <div className="flex flex-wrap items-center gap-1 p-0.5 rounded-lg bg-paper border border-line/60">
            {counts.issues > 0 && (
              <button
                type="button"
                onClick={() => {
                  setActiveTab('issues');
                  setViewMode('cards');
                }}
                className={cn(
                  'px-2.5 py-1 rounded-md text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer',
                  activeTab === 'issues'
                    ? 'bg-red-500/15 text-red-700 dark:text-red-300 shadow-2xs'
                    : 'text-ink-faint hover:text-red-600',
                )}
              >
                <XCircle size={12} />
                Issues ({String(counts.issues)})
              </button>
            )}

            <button
              type="button"
              onClick={() => { setActiveTab('all'); }}
              className={cn(
                'px-2.5 py-1 rounded-md text-xs font-bold transition-all cursor-pointer',
                activeTab === 'all'
                  ? 'bg-slate/15 text-ink shadow-2xs'
                  : 'text-ink-faint hover:text-ink',
              )}
            >
              All Records ({String(counts.all)})
            </button>

            <button
              type="button"
              onClick={() => { setActiveTab('ready'); }}
              className={cn(
                'px-2.5 py-1 rounded-md text-xs font-bold transition-all flex items-center gap-1 cursor-pointer',
                activeTab === 'ready'
                  ? 'bg-green-500/15 text-green-700 dark:text-green-300 shadow-2xs'
                  : 'text-ink-faint hover:text-green-600',
              )}
            >
              <CheckCircle2 size={12} />
              Ready ({String(counts.ready)})
            </button>

            {counts.associates > 0 && (
              <button
                type="button"
                onClick={() => { setActiveTab('associates'); }}
                className={cn(
                  'px-2.5 py-1 rounded-md text-xs font-bold transition-all flex items-center gap-1 cursor-pointer',
                  activeTab === 'associates'
                    ? 'bg-blue-500/15 text-blue-700 dark:text-blue-300 shadow-2xs'
                    : 'text-ink-faint hover:text-blue-600',
                )}
              >
                <Users size={12} />
                Family / Group ({String(counts.associates)})
              </button>
            )}
          </div>

          {/* Search box & View Switcher */}
          <div className="flex items-center gap-2">
            <div className="relative w-full sm:w-56">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint" />
              <Input
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); }}
                placeholder="Search records…"
                className="pl-7 text-xs h-8 w-full"
              />
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center p-0.5 rounded-lg bg-paper border border-line/60 shrink-0">
              <button
                type="button"
                onClick={() => { setViewMode('cards'); }}
                title="Guided Cards View"
                className={cn(
                  'p-1.5 rounded-md transition-colors cursor-pointer',
                  viewMode === 'cards'
                    ? 'bg-slate/15 text-ink'
                    : 'text-ink-faint hover:text-ink',
                )}
              >
                <LayoutGrid size={14} />
              </button>
              <button
                type="button"
                onClick={() => { setViewMode('table'); }}
                title="Spreadsheet Table View"
                className={cn(
                  'p-1.5 rounded-md transition-colors cursor-pointer',
                  viewMode === 'table'
                    ? 'bg-slate/15 text-ink'
                    : 'text-ink-faint hover:text-ink',
                )}
              >
                <TableIcon size={14} />
              </button>
            </div>
          </div>
        </div>
      </SurfaceCard>

      {/* ─── Mode 1: Friendly Guided Cards View ─────────────────────────────── */}
      {viewMode === 'cards' ? (
        <div className="space-y-3">
          {filteredRows.length === 0 ? (
            <SurfaceCard className="p-12 text-center border border-line/60">
              <CheckCircle2 size={32} className="text-green-500 mx-auto mb-2" />
              <p className="text-xs font-bold text-ink">No records found for this view</p>
            </SurfaceCard>
          ) : (
            filteredRows.map((row) => {
              const isFailed = row.status === 'FAILED';
              const isAssociate = row.isAssociatePolicy === true || row.status === 'ASSOCIATE';
              const v = (row.vehicleNumber ?? '').toUpperCase().trim();
              const isGroupVeh = v === 'GROUP' || v.includes('GROUP');
              const isAutoVeh = v === 'AUTO';

              return (
                <SurfaceCard
                  key={row.rowNumber}
                  className={cn(
                    'p-4 border transition-all',
                    isFailed
                      ? 'border-red-500/40 bg-red-500/5'
                      : isAssociate
                        ? 'border-blue-500/30 bg-blue-500/5'
                        : 'border-line/60 bg-paper',
                  )}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    {/* Left: Row info & summary */}
                    <div className="space-y-1.5 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-xs font-bold text-ink-faint">
                          #{String(row.rowNumber)}
                        </span>
                        <h4 className="text-xs font-bold text-ink">
                          {row.clientName}
                        </h4>

                        {/* Status badge */}
                        {isFailed ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500/15 text-red-700 dark:text-red-300">
                            <AlertTriangle size={11} /> Issue
                          </span>
                        ) : isAssociate ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-500/15 text-blue-700 dark:text-blue-300">
                            <Users size={11} /> Family / Group
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-500/15 text-green-700 dark:text-green-300">
                            <CheckCircle2 size={11} /> Ready
                          </span>
                        )}

                        <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-surface border border-line/60 text-ink">
                          {row.policyTypeName}
                        </span>
                      </div>

                      {/* Detail chips */}
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-soft">
                        <span className="flex items-center gap-1">
                          <Phone size={12} className="text-ink-faint" />
                          <span className="font-mono text-[11px]">{row.mobileNumber ?? '—'}</span>
                        </span>

                        {row.vehicleNumber && (
                          <span className="flex items-center gap-1">
                            <Car size={12} className="text-ink-faint" />
                            <span className="font-mono text-[11px] font-bold text-amber-700 dark:text-amber-300">
                              {row.vehicleNumber}
                            </span>
                          </span>
                        )}

                        {row.policyNumber && (
                          <span className="flex items-center gap-1">
                            <Shield size={12} className="text-ink-faint" />
                            <span className="font-mono text-[11px]">{row.policyNumber}</span>
                          </span>
                        )}

                        <span className="flex items-center gap-1">
                          <Calendar size={12} className="text-ink-faint" />
                          <span className="font-mono text-[11px]">{row.endDate || '—'}</span>
                        </span>
                      </div>

                      {/* Prominent Issue Reason */}
                      {isFailed && row.reason && (
                        <div className="pt-1">
                          <p className="text-xs font-semibold text-red-600 dark:text-red-400 flex items-center gap-1.5">
                            <AlertTriangle size={13} className="shrink-0" />
                            {row.reason}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Right: Actions */}
                    <div className="flex flex-wrap items-center gap-2 self-start sm:self-center shrink-0">
                      {/* Smart 1-click fixes right on the card */}
                      {isGroupVeh && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            handleSaveRowFromModal(row.rowNumber, {
                              policyTypeName: 'Group Insurance',
                              vehicleNumber: null,
                            });
                          }}
                          leftIcon={<Sparkles size={12} />}
                          className="text-xs font-bold"
                        >
                          Convert to Group Insurance
                        </Button>
                      )}

                      {isAutoVeh && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            handleSaveRowFromModal(row.rowNumber, {
                              policyTypeName: 'Motor',
                              vehicleNumber: null,
                            });
                          }}
                          leftIcon={<Sparkles size={12} />}
                          className="text-xs font-bold"
                        >
                          Set Motor &amp; Clear Tag
                        </Button>
                      )}

                      <Button
                        size="sm"
                        variant={isFailed ? 'primary' : 'outline'}
                        onClick={() => { openEditModal(row); }}
                        leftIcon={<Edit2 size={12} />}
                        className="text-xs font-bold"
                      >
                        {isFailed ? 'Fix Record' : 'Edit'}
                      </Button>
                    </div>
                  </div>
                </SurfaceCard>
              );
            })
          )}
        </div>
      ) : (
        /* ─── Mode 2: Enhanced Spreadsheet Table View ──────────────────────── */
        <SurfaceCard className="p-0 border border-line/80 rounded-xl overflow-hidden shadow-xs">
          {/* Table Quick Header / Stats bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3.5 bg-surface/60 border-b border-line text-xs">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="flex items-center gap-2 font-medium text-ink">
                <Sparkles size={14} className="text-slate shrink-0" />
                <span>Double-click any cell to inline edit &bull; Click <strong className="font-bold text-ink underline decoration-slate/40 underline-offset-2">Edit</strong> or <strong className="font-bold text-ink underline decoration-slate/40 underline-offset-2">Fix</strong> for full modal</span>
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate/10 text-xs font-bold text-ink">
                ↔ Scroll horizontally for all columns
              </span>
            </div>
            <span className="font-mono text-xs text-ink-faint font-bold">
              Showing {filteredRows.length} of {rowStatuses.length} records
            </span>
          </div>

          <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-310px)] min-h-[320px]">
            <table className="bulk-review-table w-max min-w-full text-left border-collapse text-xs">
              <thead className="sticky top-0 z-20 bg-surface/95 backdrop-blur-md border-b-2 border-line shadow-xs">
                <tr className="text-xs font-bold text-ink uppercase tracking-wider">
                  <th className="py-2.5 px-3.5 text-center whitespace-nowrap align-middle">#</th>
                  <th className="py-2.5 px-4 whitespace-nowrap align-middle">Status</th>
                  <th className="py-2.5 px-4 whitespace-nowrap align-middle">Insured Name</th>
                  <th className="py-2.5 px-4 whitespace-nowrap align-middle">Mobile</th>
                  <th className="py-2.5 px-4 whitespace-nowrap align-middle">Policy Type</th>
                  <th className="py-2.5 px-4 whitespace-nowrap align-middle">Vehicle No</th>
                  <th className="py-2.5 px-4 whitespace-nowrap align-middle">Policy Number</th>
                  <th className="py-2.5 px-4 whitespace-nowrap align-middle">Expiry Date</th>
                  <th className="py-2.5 px-4 whitespace-nowrap align-middle">Reference / Reason</th>
                  <th className="py-2.5 px-4 text-center whitespace-nowrap align-middle">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line/50">
                {filteredRows.map((row) => {
                  const isFailed = row.status === 'FAILED';
                  const isAssociate = row.isAssociatePolicy === true || row.status === 'ASSOCIATE';

                  return (
                    <tr
                      key={row.rowNumber}
                      className={cn(
                        'transition-colors hover:bg-slate/8',
                        isFailed
                          ? 'bg-red-500/5'
                          : isAssociate
                            ? 'bg-blue-500/5'
                            : '',
                      )}
                    >
                      {/* Row # */}
                      <td className="py-2.5 px-3.5 font-mono text-xs text-center text-ink-faint font-semibold whitespace-nowrap align-middle">
                        {row.rowNumber}
                      </td>

                      {/* Status */}
                      <td className="py-2.5 px-4 whitespace-nowrap align-middle">
                        {isFailed ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-rose-500/15 text-rose-700 dark:text-rose-300 border border-rose-500/25 whitespace-nowrap">
                            <AlertTriangle size={12} className="shrink-0" />
                            Issue
                          </span>
                        ) : isAssociate ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-blue-500/15 text-blue-700 dark:text-blue-300 border border-blue-500/25 whitespace-nowrap">
                            <Users size={12} className="shrink-0" />
                            Associate
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/25 whitespace-nowrap">
                            <CheckCircle2 size={12} className="shrink-0" />
                            Ready
                          </span>
                        )}
                      </td>

                      {/* Insured Name (Editable) */}
                      <td
                        onDoubleClick={() => { startEditing(row.rowNumber, 'insuranceHolderName', row.clientName); }}
                        className="py-2.5 px-4 font-semibold text-ink group cursor-pointer whitespace-nowrap align-middle"
                        title="Double-click to quick-edit name"
                      >
                        {editingCell?.rowNumber === row.rowNumber && editingCell.field === 'insuranceHolderName' ? (
                          <div className="flex items-center gap-1.5 min-w-[220px]">
                            <input
                              autoFocus
                              value={editValue}
                              onChange={(e) => { setEditValue(e.target.value); }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') commitEdit();
                                if (e.key === 'Escape') cancelEdit();
                              }}
                              onBlur={commitEdit}
                              className="px-2.5 py-1.5 rounded-md border border-slate bg-paper text-xs font-semibold w-full focus:outline-none focus:ring-1 focus:ring-slate"
                            />
                            <button type="button" onClick={commitEdit} className="p-1.5 rounded text-green-600 hover:bg-green-500/10 shrink-0">
                              <Check size={14} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between gap-3">
                            <span className="font-semibold text-ink whitespace-nowrap" title={row.clientName}>
                              {row.clientName}
                            </span>
                            <Edit2 size={11} className="text-ink-faint opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                          </div>
                        )}
                      </td>

                      {/* Mobile (Editable) */}
                      <td
                        onDoubleClick={() => { startEditing(row.rowNumber, 'mobileNumber', row.mobileNumber ?? ''); }}
                        className="py-2.5 px-4 font-mono text-xs text-ink group cursor-pointer whitespace-nowrap align-middle"
                        title="Double-click to quick-edit mobile"
                      >
                        {editingCell?.rowNumber === row.rowNumber && editingCell.field === 'mobileNumber' ? (
                          <div className="flex items-center gap-1.5 min-w-[140px]">
                            <input
                              autoFocus
                              value={editValue}
                              onChange={(e) => { setEditValue(e.target.value); }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') commitEdit();
                                if (e.key === 'Escape') cancelEdit();
                              }}
                              onBlur={commitEdit}
                              className="px-2.5 py-1.5 rounded-md border border-slate bg-paper text-xs font-mono w-full focus:outline-none focus:ring-1 focus:ring-slate"
                            />
                            <button type="button" onClick={commitEdit} className="p-1.5 rounded text-green-600 hover:bg-green-500/10 shrink-0">
                              <Check size={14} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between gap-2.5">
                            <span className="text-ink-soft flex items-center gap-2 font-mono whitespace-nowrap">
                              <Phone size={12} className="text-ink-faint shrink-0" />
                              <span>{row.mobileNumber ?? '—'}</span>
                            </span>
                            <Edit2 size={11} className="text-ink-faint opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                          </div>
                        )}
                      </td>

                      {/* Policy Type (Editable) */}
                      <td
                        onDoubleClick={() => { startEditing(row.rowNumber, 'policyTypeName', row.policyTypeName); }}
                        className="py-2.5 px-4 font-medium text-ink group cursor-pointer whitespace-nowrap align-middle"
                        title="Double-click to quick-edit policy type"
                      >
                        {editingCell?.rowNumber === row.rowNumber && editingCell.field === 'policyTypeName' ? (
                          <div className="flex items-center gap-1.5 min-w-[140px]">
                            <input
                              autoFocus
                              value={editValue}
                              onChange={(e) => { setEditValue(e.target.value); }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') commitEdit();
                                if (e.key === 'Escape') cancelEdit();
                              }}
                              onBlur={commitEdit}
                              className="px-2.5 py-1.5 rounded-md border border-slate bg-paper text-xs font-semibold w-full focus:outline-none focus:ring-1 focus:ring-slate"
                            />
                            <button type="button" onClick={commitEdit} className="p-1.5 rounded text-green-600 hover:bg-green-500/10 shrink-0">
                              <Check size={14} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between gap-2.5">
                            <span
                              className={cn(
                                'inline-flex items-center px-2.5 py-1 rounded-md font-semibold text-xs border whitespace-nowrap',
                                getPolicyTypeBadgeClass(row.policyTypeName),
                              )}
                            >
                              {row.policyTypeName || 'Motor'}
                            </span>
                            <Edit2 size={11} className="text-ink-faint opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                          </div>
                        )}
                      </td>

                      {/* Vehicle Number (Editable) */}
                      <td
                        onDoubleClick={() => { startEditing(row.rowNumber, 'vehicleNumber', row.vehicleNumber ?? ''); }}
                        className="py-2.5 px-4 font-mono text-xs font-semibold text-ink group cursor-pointer whitespace-nowrap align-middle"
                        title="Double-click to quick-edit vehicle"
                      >
                        {editingCell?.rowNumber === row.rowNumber && editingCell.field === 'vehicleNumber' ? (
                          <div className="flex items-center gap-1.5 min-w-[140px]">
                            <input
                              autoFocus
                              value={editValue}
                              onChange={(e) => { setEditValue(e.target.value); }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') commitEdit();
                                if (e.key === 'Escape') cancelEdit();
                              }}
                              onBlur={commitEdit}
                              className="px-2.5 py-1.5 rounded-md border border-slate bg-paper text-xs font-mono uppercase w-full focus:outline-none focus:ring-1 focus:ring-slate"
                            />
                            <button type="button" onClick={commitEdit} className="p-1.5 rounded text-green-600 hover:bg-green-500/10 shrink-0">
                              <Check size={14} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between gap-2.5">
                            {row.vehicleNumber ? (
                              <span
                                className={cn(
                                  'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md font-mono text-xs font-bold tracking-wide border whitespace-nowrap',
                                  isFailed &&
                                    (row.vehicleNumber.toUpperCase() === 'GROUP' ||
                                      row.vehicleNumber.toUpperCase() === 'AUTO')
                                    ? 'bg-amber-500/15 text-amber-900 dark:text-amber-200 border-amber-500/35 font-bold'
                                    : 'bg-slate/8 text-ink-soft border-line/70 dark:bg-slate/20',
                                )}
                              >
                                <Car size={11} className="opacity-70 shrink-0" />
                                <span>{row.vehicleNumber}</span>
                              </span>
                            ) : (
                              <span className="text-ink-faint/60 font-mono text-center block">—</span>
                            )}
                            <Edit2 size={11} className="text-ink-faint opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                          </div>
                        )}
                      </td>

                      {/* Policy Number (Editable) */}
                      <td
                        onDoubleClick={() => { startEditing(row.rowNumber, 'policyNumber', row.policyNumber ?? ''); }}
                        className="py-2.5 px-4 font-mono text-xs text-ink group cursor-pointer whitespace-nowrap align-middle"
                        title="Double-click to quick-edit policy number"
                      >
                        {editingCell?.rowNumber === row.rowNumber && editingCell.field === 'policyNumber' ? (
                          <div className="flex items-center gap-1.5 min-w-[200px]">
                            <input
                              autoFocus
                              value={editValue}
                              onChange={(e) => { setEditValue(e.target.value); }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') commitEdit();
                                if (e.key === 'Escape') cancelEdit();
                              }}
                              onBlur={commitEdit}
                              className="px-2.5 py-1.5 rounded-md border border-slate bg-paper text-xs font-mono w-full focus:outline-none focus:ring-1 focus:ring-slate"
                            />
                            <button type="button" onClick={commitEdit} className="p-1.5 rounded text-green-600 hover:bg-green-500/10 shrink-0">
                              <Check size={14} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between gap-3">
                            <span className="font-mono text-xs font-medium text-ink-soft whitespace-nowrap tracking-tight" title={row.policyNumber ?? ''}>
                              {row.policyNumber ?? '—'}
                            </span>
                            <Edit2 size={11} className="text-ink-faint opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                          </div>
                        )}
                      </td>

                      {/* Expiry Date (Editable) */}
                      <td
                        onDoubleClick={() => { startEditing(row.rowNumber, 'endDate', row.endDate); }}
                        className="py-2.5 px-4 font-mono text-xs text-ink group cursor-pointer whitespace-nowrap align-middle"
                        title="Double-click to quick-edit date"
                      >
                        {editingCell?.rowNumber === row.rowNumber && editingCell.field === 'endDate' ? (
                          <div className="flex items-center gap-1.5 min-w-[130px]">
                            <input
                              autoFocus
                              value={editValue}
                              onChange={(e) => { setEditValue(e.target.value); }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') commitEdit();
                                if (e.key === 'Escape') cancelEdit();
                              }}
                              onBlur={commitEdit}
                              className="px-2.5 py-1.5 rounded-md border border-slate bg-paper text-xs font-mono w-full focus:outline-none focus:ring-1 focus:ring-slate"
                            />
                            <button type="button" onClick={commitEdit} className="p-1.5 rounded text-green-600 hover:bg-green-500/10 shrink-0">
                              <Check size={14} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between gap-2.5">
                            <span className="font-mono text-xs text-ink-soft flex items-center gap-2 whitespace-nowrap">
                              <Calendar size={12} className="text-ink-faint shrink-0" />
                              <span>{row.endDate && row.endDate.length > 0 ? row.endDate : '—'}</span>
                            </span>
                            <Edit2 size={11} className="text-ink-faint opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                          </div>
                        )}
                      </td>

                      {/* Notes / Reason */}
                      <td className="py-2.5 px-4 text-xs whitespace-nowrap align-middle">
                        {isFailed ? (
                          <div className="flex items-center gap-2 text-rose-600 dark:text-rose-400 font-semibold whitespace-nowrap" title={row.reason ?? ''}>
                            <AlertTriangle size={13} className="shrink-0" />
                            <span>{row.reason}</span>
                          </div>
                        ) : (
                          <span
                            className="text-ink-faint text-xs whitespace-nowrap"
                            title={row.referenceNote ?? row.referenceName ?? ''}
                          >
                            {row.referenceNote != null && row.referenceNote.length > 0
                              ? row.referenceNote
                              : row.referenceName != null && row.referenceName.length > 0
                                ? row.referenceName
                                : 'Data cleaned & normalized'}
                          </span>
                        )}
                      </td>

                      {/* Action Button */}
                      <td className="py-2.5 px-4 text-center whitespace-nowrap align-middle">
                        <button
                          type="button"
                          onClick={() => { openEditModal(row); }}
                          className={cn(
                            'inline-flex items-center justify-center gap-1.5 h-7 px-2.5 rounded-md text-[11px] font-semibold tracking-wide transition-all duration-150 cursor-pointer shadow-2xs',
                            isFailed
                              ? 'bg-rose-600 hover:bg-rose-700 text-white shadow-[0_2px_8px_rgba(225,29,72,0.3)] hover:brightness-105 active:scale-[0.98]'
                              : 'bg-paper text-ink border border-line-strong hover:bg-surface hover:border-slate hover:text-slate active:scale-[0.98]',
                          )}
                        >
                          <Edit2 size={11} className="shrink-0" />
                          <span>{isFailed ? 'Fix' : 'Edit'}</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </SurfaceCard>
      )}

      {/* ─── Bottom Actions Bar (Sticky & Always in View) ─────────────────────── */}
      <div className="sticky bottom-0 z-30 bg-paper/95 backdrop-blur-md py-2.5 px-4 rounded-xl border border-line/80 shadow-md flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={onBackToMapping}
          leftIcon={<RotateCcw size={14} />}
          className="w-full sm:w-auto font-semibold text-xs h-9"
        >
          Back to Mapping
        </Button>

        <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
          <Button
            variant="primary"
            size="sm"
            disabled={counts.ready === 0 || isImporting}
            onClick={onConfirmImport}
            leftIcon={<ShieldCheck size={15} />}
            className="w-full sm:w-auto font-bold text-xs h-9 shadow-sm"
          >
            {isImporting
              ? 'Saving to Database…'
              : counts.issues > 0
                ? `Confirm & Import ${String(counts.ready)} Valid Records`
                : `Confirm & Import All ${String(counts.all)} Policies`}
          </Button>
        </div>
      </div>

      {/* ─── Non-technical User Friendly Edit Modal ──────────────────────────── */}
      <EditRowModal
        row={selectedRowForModal}
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedRowForModal(null);
        }}
        onSave={handleSaveRowFromModal}
        onSaveAndNext={handleSaveAndNext}
        hasNextIssue={failedRows.length > 1}
      />
    </div>
  );
}
